import type {
  ConfigAuditPage,
  ConfigReceipt,
  MonitoringConfiguration,
  MonitoringNetwork,
  ObservationBasis,
  WarningConfiguration,
} from '../consumer/configuration';
import { METRIC_SPEC } from '../consumer/configuration';
type Validator = (value: unknown) => void;
function invalid(): never {
  throw new Error('Invalid NormalMonitoringConfigurationV1 response');
}
const text: Validator = (v) => {
  if (typeof v !== 'string' || v.length > 16384) invalid();
};
const id: Validator = (v) => {
  text(v);
  if (v === '') invalid();
};
const count: Validator = (v) => {
  if (typeof v !== 'number' || !Number.isSafeInteger(v) || v < 0) invalid();
};
const positive: Validator = (v) => {
  count(v);
  if (v === 0) invalid();
};
const bool: Validator = (v) => {
  if (typeof v !== 'boolean') invalid();
};
const time: Validator = (v) => {
  id(v);
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(v as string) ||
    !Number.isFinite(Date.parse(v as string))
  )
    invalid();
};
const nullable =
  (validate: Validator): Validator =>
  (v) => {
    if (v !== null) validate(v);
  };
const oneOf =
  (...values: unknown[]): Validator =>
  (v) => {
    if (!values.includes(v)) invalid();
  };
const array =
  (validate: Validator, max = 10000): Validator =>
  (v) => {
    if (!Array.isArray(v) || v.length > max) invalid();
    (v as unknown[]).forEach(validate);
  };
const record =
  (shape: Record<string, Validator>): Validator =>
  (v) => {
    if (v === null || typeof v !== 'object' || Array.isArray(v)) invalid();
    const obj = v as Record<string, unknown>;
    if (
      Object.keys(obj).length !== Object.keys(shape).length ||
      Object.keys(obj).some((key) => !Object.hasOwn(shape, key))
    )
      invalid();
    Object.entries(shape).forEach(([key, validate]) => validate(obj[key]));
  };
const metric = oneOf(...Object.keys(METRIC_SPEC));
const instance = oneOf('forest-demo-1');
const sourceMode = oneOf('SYNTHETIC_OBSERVATIONS');
const area = record({
  areaId: id,
  instanceId: instance,
  displayName: id,
  revision: positive,
  createdAt: time,
});
const source = record({
  sourceId: id,
  instanceId: instance,
  areaId: id,
  displayName: id,
  metric,
  unit: id,
  category: id,
  sourceMode,
  enabled: bool,
  revision: positive,
  epoch: count,
  enabledAt: nullable(time),
});
const config = record({
  instanceId: instance,
  configRevision: positive,
  generatedAt: time,
  areas: array(area, 100),
  sources: array(source, 1000),
});
const coverage = record({
  areaId: id,
  configuredSources: count,
  enabledSources: count,
  recentSources: count,
  eligibleSources: count,
  coveredMetrics: array(metric, 3),
  missingMetrics: array(metric, 3),
  coverage: oneOf('NO_SOURCES', 'ALL_DISABLED', 'COVERAGE_GAP', 'COMPLETE'),
});
const runtime = record({
  sourceId: id,
  epoch: count,
  receiveHealth: oneOf('NO_DATA', 'RECENT', 'STALE'),
  eligible: bool,
  normalSamples: count,
  latestObservedAt: nullable(time),
  latestReceivedAt: nullable(time),
});
const network = record({
  configRevision: positive,
  generatedAt: time,
  sourceMode,
  areas: array(coverage, 100),
  sources: array(runtime, 1000),
});
const receipt = record({
  requestId: id,
  action: id,
  objectId: id,
  configRevision: positive,
  objectRevision: positive,
  epoch: count,
  recordedAt: time,
});
const audit = record({
  requestId: id,
  action: id,
  objectId: id,
  beforeRevision: count,
  afterRevision: positive,
  beforeConfigRevision: positive,
  afterConfigRevision: positive,
  actor: id,
  reason: text,
  recordedAt: time,
});
const history = record({
  items: array(audit, 100),
  total: count,
  offset: count,
  limit: positive,
  generatedAt: time,
});
const observation = record({
  reference: id,
  configRevision: count,
  sourceRevision: count,
  epoch: count,
  basis: id,
  processingStatus: id,
  processingReason: text,
});
const warning = record({
  warningId: id,
  revision: positive,
  configRevision: count,
  basis: id,
  requiredSourceIds: array(id, 1000),
  missingSourceIds: array(id, 1000),
  missingMetrics: array(metric, 3),
  changeKind: id,
});
const warningConfiguration = record({
  warningId: id,
  revisions: array(warning),
  observations: array(observation),
});
export function parseConfiguration(value: unknown): MonitoringConfiguration {
  config(value);
  const data = value as MonitoringConfiguration;
  if (
    new Set(data.areas.map((a) => a.areaId)).size !== data.areas.length ||
    new Set(data.sources.map((s) => s.sourceId)).size !== data.sources.length
  )
    invalid();
  for (const s of data.sources) {
    const spec = METRIC_SPEC[s.metric];
    if (
      !data.areas.some((a) => a.areaId === s.areaId) ||
      s.unit !== spec.unit ||
      s.category !== spec.category ||
      (s.enabled && s.epoch === 0)
    )
      invalid();
  }
  return data;
}
export function parseNetwork(value: unknown): MonitoringNetwork {
  network(value);
  const data = value as MonitoringNetwork;
  if (
    new Set(data.areas.map((a) => a.areaId)).size !== data.areas.length ||
    new Set(data.sources.map((s) => s.sourceId)).size !== data.sources.length
  )
    invalid();
  for (const a of data.areas) {
    if (
      a.enabledSources > a.configuredSources ||
      a.recentSources > a.enabledSources ||
      a.eligibleSources > a.recentSources ||
      new Set([...a.coveredMetrics, ...a.missingMetrics]).size !== 3 ||
      a.coveredMetrics.length + a.missingMetrics.length !== 3
    )
      invalid();
    if (
      (a.coverage === 'NO_SOURCES') !== (a.configuredSources === 0) ||
      (a.coverage === 'ALL_DISABLED') !== (a.configuredSources > 0 && a.enabledSources === 0) ||
      (a.coverage === 'COMPLETE') !== (a.missingMetrics.length === 0)
    )
      invalid();
  }
  return data;
}
export function parseConfigReceipt(value: unknown): ConfigReceipt {
  receipt(value);
  return value as ConfigReceipt;
}
export function parseConfigHistory(value: unknown): ConfigAuditPage {
  history(value);
  const data = value as ConfigAuditPage;
  if (data.limit > 100 || data.items.length > data.limit || data.items.length > data.total)
    invalid();
  return data;
}
export function parseObservationBasis(value: unknown): ObservationBasis {
  observation(value);
  return value as ObservationBasis;
}
export function parseWarningConfiguration(value: unknown): WarningConfiguration {
  warningConfiguration(value);
  const data = value as WarningConfiguration;
  if (data.revisions.some((r) => r.warningId !== data.warningId)) invalid();
  return data;
}
